/**
 * AdminHepVideos — clinic-wide Custom Home Exercise Program video library.
 * Iter 68.
 *
 * Clinicians can record their own exercise demonstrations + paste URLs, and
 * later bundle multiple videos into a patient-specific HEP program.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { safeConfirm } from '../../lib/confirm';
import { Video, Plus, Edit2, Trash2, PlayCircle, Search, X, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';

const REGIONS = ['neck', 'shoulder', 'thoracic', 'lumbar', 'hip', 'knee', 'ankle', 'foot', 'wrist', 'elbow', 'core', 'full body'];
const DISCIPLINES = ['physiotherapy', 'osteopathy', 'chiropractic', 'massage_therapy', 'fitness'];

export default function AdminHepVideos() {
  const [videos, setVideos] = useState([]);
  const [q, setQ] = useState('');
  const [region, setRegion] = useState('');
  const [editVid, setEditVid] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    const params = {};
    if (region) params.body_region = region;
    const r = await api.get('/hep/videos', { params });
    setVideos(r.data || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [region]);

  const filtered = videos.filter(v => !q || v.name?.toLowerCase().includes(q.toLowerCase()));

  const del = async (id) => {
    if (!(await safeConfirm('Delete this video?'))) return;
    await api.delete(`/hep/videos/${id}`);
    setVideos(vs => vs.filter(v => v.video_id !== id));
    toast.success('Deleted.');
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">CUSTOM HEP LIBRARY</div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">My exercise videos</h1>
          <p className="text-fg-muted mt-2 text-sm">Build a library of your own exercise demos. Bundle them into a Home Exercise Program for any patient.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/hep/programs" className="btn-secondary inline-flex items-center gap-2" data-testid="hep-programs-link">
            <Users className="w-4 h-4"/> Programs
          </Link>
          <button onClick={() => setShowCreate(true)} className="btn-primary inline-flex items-center gap-2" data-testid="hep-create-btn">
            <Plus className="w-4 h-4"/> Add video
          </button>
        </div>
      </div>

      <div className="bento-card mb-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"/>
            <input value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search by name…"
                   className="input pl-9 !text-sm"
                   data-testid="hep-search"/>
          </div>
          <select value={region} onChange={(e) => setRegion(e.target.value)}
                  className="input !text-sm max-w-[200px]" data-testid="hep-region-filter">
            <option value="">All regions</option>
            {REGIONS.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bento-card text-center py-10" data-testid="hep-empty">
          <Video className="w-12 h-12 mx-auto text-fg-muted mb-3"/>
          <div className="text-fg-muted text-sm mb-3">No videos in this view.</div>
          <button onClick={() => setShowCreate(true)} className="btn-primary text-sm">Add your first video</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(v => (
            <div key={v.video_id} className="bento-card" data-testid={`hep-video-card-${v.video_id}`}>
              {v.thumbnail_url ? (
                <img src={v.thumbnail_url} alt="" className="w-full h-32 object-cover rounded-md mb-3"/>
              ) : (
                <div className="w-full h-32 rounded-md bg-brand/10 flex items-center justify-center mb-3">
                  <PlayCircle className="w-10 h-10 text-brand"/>
                </div>
              )}
              <div className="font-heading font-semibold text-sm mb-1 truncate">{v.name}</div>
              <div className="flex items-center gap-1.5 text-[10px] text-fg-muted uppercase tracking-wider mb-2">
                {v.body_region && <span className="bg-brand/10 text-brand px-1.5 py-0.5 rounded">{v.body_region}</span>}
                {v.difficulty && <span>{v.difficulty}</span>}
              </div>
              {v.description && <p className="text-xs text-fg-muted line-clamp-2 mb-3">{v.description}</p>}
              <div className="flex items-center justify-between text-xs">
                {v.video_url ? (
                  <a href={v.video_url} target="_blank" rel="noreferrer"
                     className="text-brand inline-flex items-center gap-1 hover:underline"
                     data-testid={`hep-watch-${v.video_id}`}>
                    <PlayCircle className="w-3.5 h-3.5"/> Watch
                  </a>
                ) : <span/>}
                <div className="flex gap-2">
                  <button onClick={() => setEditVid(v)} className="text-fg-muted hover:text-brand" data-testid={`hep-edit-${v.video_id}`}>
                    <Edit2 className="w-3.5 h-3.5"/>
                  </button>
                  <button onClick={() => del(v.video_id)} className="text-[#B83A3A] hover:opacity-70" data-testid={`hep-del-${v.video_id}`}>
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <VideoModal onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }}/>
      )}
      {editVid && (
        <VideoModal initial={editVid} onClose={() => setEditVid(null)} onSaved={() => { setEditVid(null); load(); }}/>
      )}
    </div>
  );
}

function VideoModal({ initial, onClose, onSaved }) {
  // Iter 73 — Source toggle: 'url' (paste a YouTube/Vimeo/MP4 URL) or 'upload'
  // (multipart upload from the clinician's phone camera).
  const [source, setSource] = useState(initial?.source === 'upload' ? 'upload' : 'url');
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [form, setForm] = useState({
    name: initial?.name || '',
    video_url: initial?.video_url || '',
    thumbnail_url: initial?.thumbnail_url || '',
    description: initial?.description || '',
    instructions: initial?.instructions || '',
    body_region: initial?.body_region || '',
    discipline: initial?.discipline || 'physiotherapy',
    equipment: (initial?.equipment || []).join(', '),
    difficulty: initial?.difficulty || 'beginner',
    duration_sec: initial?.duration_sec || '',
    is_public: initial?.is_public || false,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name) { toast.error('Name is required.'); return; }
    // URL path needs a URL. Upload path needs a file (or an existing record).
    if (source === 'url' && !form.video_url) {
      toast.error('Video URL is required.'); return;
    }
    if (source === 'upload' && !initial && !file) {
      toast.error('Pick a video file from your camera.'); return;
    }
    setSaving(true);
    try {
      if (source === 'upload' && file) {
        // Multipart upload — server streams the bytes into Emergent Object Storage.
        const fd = new FormData();
        fd.append('file', file);
        fd.append('name', form.name);
        if (form.description) fd.append('description', form.description);
        if (form.instructions) fd.append('instructions', form.instructions);
        if (form.body_region) fd.append('body_region', form.body_region);
        if (form.discipline) fd.append('discipline', form.discipline);
        if (form.difficulty) fd.append('difficulty', form.difficulty);
        if (form.duration_sec) fd.append('duration_sec', form.duration_sec);
        fd.append('is_public', form.is_public ? 'true' : 'false');
        await api.post('/hep/videos/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        toast.success('Video uploaded.');
      } else {
        const payload = {
          ...form,
          equipment: (form.equipment || '').split(',').map(s => s.trim()).filter(Boolean),
          duration_sec: form.duration_sec ? parseInt(form.duration_sec) : null,
        };
        if (initial) {
          await api.put(`/hep/videos/${initial.video_id}`, payload);
          toast.success('Saved.');
        } else {
          await api.post('/hep/videos', payload);
          toast.success('Video added.');
        }
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); setUploadProgress(0); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center pt-16 px-4" onClick={onClose}>
      <div className="bg-bg-primary border border-soft rounded-xl w-full max-w-xl max-h-[85vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="hep-modal">
        <div className="sticky top-0 bg-bg-primary border-b border-soft p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <Video className="w-4 h-4 text-brand"/>
            <h3 className="font-heading font-semibold">{initial ? 'Edit' : 'Add'} exercise video</h3>
          </div>
          <button onClick={onClose} className="text-fg-muted hover:text-fg"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-4 space-y-3">
          {/* Iter 73 — source picker. Mobile users hit "Record / Upload" to use the
              device camera; desktop users typically paste a URL. */}
          {!initial && (
            <div className="flex gap-1 p-1 rounded-lg border border-soft bg-bg-secondary/40" data-testid="hep-source-toggle">
              <button type="button" onClick={() => setSource('url')}
                      className={`flex-1 text-xs py-1.5 rounded transition ${source === 'url' ? 'bg-bg-primary shadow-sm font-semibold' : 'text-fg-muted'}`}
                      data-testid="hep-source-url">
                Paste URL
              </button>
              <button type="button" onClick={() => setSource('upload')}
                      className={`flex-1 text-xs py-1.5 rounded transition ${source === 'upload' ? 'bg-bg-primary shadow-sm font-semibold' : 'text-fg-muted'}`}
                      data-testid="hep-source-upload">
                Record / Upload
              </button>
            </div>
          )}
          <Field label="Name">
            <input className="input" placeholder='e.g. "Cat-Cow with Breath"' value={form.name}
                   onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                   data-testid="hep-modal-name"/>
          </Field>
          {source === 'url' ? (
            <>
              <Field label="Video URL (YouTube / Vimeo / direct MP4)">
                <input className="input" placeholder="https://youtube.com/watch?v=…" value={form.video_url}
                       onChange={(e) => setForm(f => ({ ...f, video_url: e.target.value }))}
                       data-testid="hep-modal-url"/>
              </Field>
              <Field label="Thumbnail URL (optional)">
                <input className="input" placeholder="https://…/thumb.jpg" value={form.thumbnail_url}
                       onChange={(e) => setForm(f => ({ ...f, thumbnail_url: e.target.value }))}/>
              </Field>
            </>
          ) : (
            <Field label="Video file (record from your phone camera)">
              <input type="file" accept="video/*" capture="environment"
                     onChange={(e) => setFile(e.target.files?.[0] || null)}
                     className="block w-full text-sm text-fg file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-brand file:text-white file:cursor-pointer hover:file:bg-brand/90"
                     data-testid="hep-modal-file"/>
              <div className="text-[11px] text-fg-muted mt-1">
                {file
                  ? <>Selected: <strong>{file.name}</strong> ({Math.round(file.size / 1024)} KB)</>
                  : 'Tap to choose a video from your phone — most mobile browsers will open the camera. Up to 200 MB.'}
              </div>
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="mt-2">
                  <div className="h-1.5 rounded-full bg-bg-secondary overflow-hidden">
                    <div className="h-full bg-brand transition-all" style={{ width: `${uploadProgress}%` }}/>
                  </div>
                  <div className="text-[11px] text-fg-muted mt-1">{uploadProgress}% uploaded…</div>
                </div>
              )}
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Body region">
              <select className="input" value={form.body_region}
                      onChange={(e) => setForm(f => ({ ...f, body_region: e.target.value }))}>
                <option value="">— None —</option>
                {REGIONS.map(r => <option key={r} value={r} className="capitalize">{r}</option>)}
              </select>
            </Field>
            <Field label="Discipline">
              <select className="input" value={form.discipline}
                      onChange={(e) => setForm(f => ({ ...f, discipline: e.target.value }))}>
                {DISCIPLINES.map(d => <option key={d} value={d} className="capitalize">{d.replace('_', ' ')}</option>)}
              </select>
            </Field>
            <Field label="Difficulty">
              <select className="input" value={form.difficulty}
                      onChange={(e) => setForm(f => ({ ...f, difficulty: e.target.value }))}>
                <option>beginner</option><option>intermediate</option><option>advanced</option>
              </select>
            </Field>
            <Field label="Duration (sec)">
              <input type="number" className="input" value={form.duration_sec}
                     onChange={(e) => setForm(f => ({ ...f, duration_sec: e.target.value }))}/>
            </Field>
          </div>
          <Field label="Equipment (comma-separated)">
            <input className="input" placeholder="band, foam roller" value={form.equipment}
                   onChange={(e) => setForm(f => ({ ...f, equipment: e.target.value }))}/>
          </Field>
          <Field label="Description (optional)">
            <textarea className="input min-h-[60px] !text-sm" value={form.description}
                      onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}/>
          </Field>
          <Field label="Patient instructions (cue + form points)">
            <textarea className="input min-h-[60px] !text-sm" value={form.instructions}
                      onChange={(e) => setForm(f => ({ ...f, instructions: e.target.value }))}/>
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.is_public}
                   onChange={(e) => setForm(f => ({ ...f, is_public: e.target.checked }))}
                   className="accent-brand"/>
            Share with other clinics on the platform (becomes public)
          </label>
        </div>
        <div className="p-4 border-t border-soft flex justify-end gap-2 sticky bottom-0 bg-bg-primary">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="hep-modal-save">
            {saving && <Loader2 className="w-3 h-3 animate-spin"/>}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-fg-muted mb-1">{label}</div>
      {children}
    </label>
  );
}
